/**
 * Detect the in-view XPLAN client id from `window.XPLAN.apex.entityInfo` at
 * Porter launch. XPLAN exposes the entity currently on screen on this global
 * object, so Porter reads it directly instead of parsing the page URL.
 *
 * Detection rule: a client is detected only when BOTH hold —
 *  1. `entityInfo.entityRole` is exactly `client` (other roles such as
 *     `partner` are not treated as the in-view client), AND
 *  2. `entityInfo.entityId` is present and resolves to a non-empty, all-digits
 *     id (coerced from number or string).
 *
 * Any other state — the global missing, a non-client role, or a missing /
 * non-numeric id — returns `null`, which the caller treats as "no client
 * detected" (open with no context).
 *
 * The returned id is a *candidate* only — the backend client-details resolve
 * is the authoritative check that it is a real client the adviser may see.
 *
 * Pure and side-effect-free (reads the global but mutates nothing) so it stays
 * trivially unit-testable and never touches the network.
 */
export function detectClientFromEntityInfo(): string | null {
  const entityInfo = globalThis.XPLAN?.apex?.entityInfo;
  if (!entityInfo) return null;

  // Guard 1: only the `client` role represents the in-view client.
  if (entityInfo.entityRole !== 'client') return null;

  // Guard 2: require a present, all-digits id. `entityId` may arrive as a
  // number or a string, so normalise to string before validating.
  const { entityId } = entityInfo;
  if (entityId === null || entityId === undefined) return null;

  const candidate = String(entityId);
  if (!/^\d+$/.test(candidate)) return null;

  return candidate;
}
